/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  SvgIcon,
  Box,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  CustomIcons,
  ContextMenuItemType,
  getTotalBudgetString,
  getPPPDString,
  getBudgetFlexibilityString,
} from '@marageti/z4-lib';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { titleCase } from '../../../../utils/helpers';
import { TravelerChips, TripRequestStatusChip } from '../../chips';
import { ChatIndicator } from '../../trip-indicators';
import ContextMenuComplete from '../../context-menu';

const formatCountryCodesToString = (countryCodes: string[], countryCodeMap: { [key: string]: string }) => {
  let countryNames: string[] = [];
  if (countryCodes) {
    countryNames = countryCodes.map((c) => countryCodeMap[c]);
  }
  return countryNames.join(', ');
};

const {
  OutlinedLocationMarkerIcon,
  OutlinedCurrencyDollarIcon,
  OutlinedUserIcon,
  OutlinedInboxInIcon,
} = OutlinedIcons;

const {
  DepartureIcon,
} = CustomIcons;

const IconLabel = ({ icon, label }: { icon: any, label: any }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <SvgIcon color="disabled" component={icon} />
    <Typography variant="caption">
      {label}
    </Typography>
  </Stack>
);

type AllCardViewProps = {
  countryCodes: CountryCodeMap | undefined;
  tripRequest: TripRequest;
  contextMenuOptions?: ContextMenuItemType[] | undefined;
};

const AllCardView = ({
  countryCodes,
  tripRequest,
  contextMenuOptions,
}: AllCardViewProps) => {
  const {
    countries,
    assignedTo,
    travelers,
    departureDate: trDepartureDate,
    totalNights: trTotalNights,
    numberOfAdults,
    numberOfChildren,
    totalBudget,
    createdAt,
    referralType,
    repeatTraveler,
    tripMessageState,
    pppd,
    assignedToName,
    budgetFlexibility,
    status,
  } = tripRequest;
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const fullName = titleCase(travelers[0].fullName);
  const countryNames = countryCodes ? formatCountryCodesToString(countries, countryCodes) : '';
  const departureDate = getFormattedDateShortMonthDDYYYY(trDepartureDate) || '';
  const totalNights = `${trTotalNights} Nights`;
  const numberOfTravelers = numberOfAdults + numberOfChildren;
  const totalTravelers = numberOfTravelers === 1
    ? '1 Traveler'
    : `${numberOfTravelers} Travelers`;
  const submittedDate = getFormattedDateShortMonthDDYYYY(createdAt) || '';

  return (
    <Card
      component={Link}
      sx={{
        '&:hover': {
          bgcolor: 'grey.A100',
        },
        textDecoration: 'none',
      }}
      to={`/trip-requests/${tripRequest?.id}`}
    >
      <CardContent>
        {isMobile ? (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1}>
              <Typography variant="bodyBold">
                {fullName}
              </Typography>
              <TravelerChips
                travelers={travelers}
                referralType={referralType}
                repeatTraveler={repeatTraveler}
              />
            </Stack>

            <Stack direction="column" spacing={1}>
              <IconLabel icon={OutlinedLocationMarkerIcon} label={countryNames} />
              <IconLabel
                icon={DepartureIcon}
                label={(
                  <>
                    {departureDate}
                    {' '}
                    ·
                    {' '}
                    {totalNights}
                  </>
                )}
              />
              <IconLabel
                icon={OutlinedCurrencyDollarIcon}
                label={(
                  <>
                    {getTotalBudgetString(totalBudget)}
                    {' '}
                    Total Budget
                    {' '}
                    ·
                    {' '}
                    {getPPPDString(pppd)}
                    {budgetFlexibility ? ` · ${getBudgetFlexibilityString(budgetFlexibility)}` : ''}
                  </>
                )}
              />
              <IconLabel
                icon={OutlinedInboxInIcon}
                label={`Requested: ${submittedDate}`}
              />
              <IconLabel icon={OutlinedUserIcon} label={assignedToName || '-'} />
            </Stack>

            <Grid
              container
              alignItems="center"
            >
              <Grid item xs={6} display="flex">
                <TripRequestStatusChip status={status} />
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={1.5}>
                  <ChatIndicator
                    assignedTo={assignedTo}
                    createdAt={createdAt}
                    tripMessageState={tripMessageState}
                  />
                  {contextMenuOptions && (
                  <Box sx={{ marginLeft: '1rem' }}>
                    <ContextMenuComplete
                      id={tripRequest.id}
                      items={contextMenuOptions}
                    />
                  </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Grid container direction="row" alignItems="center" columnSpacing={1.5}>
            <Grid item xs={3}>
              <Stack direction="row" spacing={1}>
                <Typography variant="bodyBold">
                  {fullName}
                </Typography>
                <TravelerChips
                  travelers={travelers}
                  referralType={referralType}
                  repeatTraveler={repeatTraveler}
                />
              </Stack>
              <Typography variant="caption">
                {countryNames}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {departureDate}
              </Typography>
              <Typography variant="caption">
                {totalNights}
                {' '}
                ·
                {' '}
                {totalTravelers}
              </Typography>
            </Grid>

            <Grid item xs={2.5}>
              <Typography variant="body">
                {getPPPDString(pppd)}
              </Typography>
              <Typography variant="caption">
                {`${getTotalBudgetString(totalBudget)} Total Budget${budgetFlexibility ? ` · ${getBudgetFlexibilityString(budgetFlexibility)}` : ''}`}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {assignedToName || '-'}
              </Typography>
              <Typography variant="caption">
                Requested:
                {' '}
                {submittedDate}
              </Typography>
            </Grid>

            <Grid item xs={1.25}>
              <Stack direction="row" justifyContent="space-around">
                <ChatIndicator
                  assignedTo={assignedTo}
                  createdAt={createdAt}
                  tripMessageState={tripMessageState}
                />
              </Stack>
            </Grid>
            <Grid item xs={1.25} display="flex" justifyContent="flex-end" alignItems="center">
              <TripRequestStatusChip status={status} />
              {contextMenuOptions && (
                <Box sx={{ marginLeft: '1rem' }}>
                  <ContextMenuComplete
                    id={tripRequest.id}
                    items={contextMenuOptions}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default AllCardView;
